import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { articleQueries } from '../../../graphql/queries';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const COLUMNS = [
  { id: 'email', label: 'Email' },
  { id: 'name', label: 'Name' },
  { id: 'sentAt', label: 'Sent at' }
];

// Inner content is mounted ONLY while the dialog is open (see ArticleRecipientsDialog), so the
// live Klaviyo read fires on open — not on every article-table render. The whole bounded list
// comes back in one payload; search + sort + paging are entirely client-side (Option A). When a
// list outgrows a single live scan (nearing the read method's page cap / the Lambda timeout),
// this is the seam to move to a server-paged article_recipients table — see the phase plan.
function RecipientsContent ({ articleId }) {
  const { data, loading, error } = useQuery(
    articleQueries.ArticleRecipientsAdmin(articleId),
    { fetchPolicy: 'no-cache' }
  );

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('sentAt');
  const [orderByDirection, setOrderByDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const recipients = (data && data.articleRecipientsAdmin && data.articleRecipientsAdmin.recipients) || [];

  // search (email + name) -> sort, memoised so paging/typing does not re-walk the list on
  // unrelated renders. sentAt is an ISO string, so a string compare sorts it chronologically.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = term
      ? recipients.filter(r =>
        `${r.email || ''} ${r.firstName || ''} ${r.lastName || ''}`.toLowerCase().includes(term))
      : recipients;

    const valueFor = (r) => {
      if (orderBy === 'email') return (r.email || '').toLowerCase();
      if (orderBy === 'name') return `${r.lastName || ''} ${r.firstName || ''}`.toLowerCase();
      return r.sentAt || '';
    };
    const dir = orderByDirection === 'asc' ? 1 : -1;
    return [...matches].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [recipients, search, orderBy, orderByDirection]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && orderByDirection === 'asc';
    setOrderBy(column);
    setOrderByDirection(isAsc ? 'desc' : 'asc');
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading recipients...</Typography>
        </Box>
      </DialogContent>
    );
  }

  // A live-read failure degrades here; the admin page and subscriber list are unaffected
  // (they carry no Klaviyo dependency).
  if (error) {
    return (
      <DialogContent>
        <Typography color='error' sx={{ py: 4 }}>
          Could not load recipients — please try again.
        </Typography>
      </DialogContent>
    );
  }

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DialogContent>
      <Typography variant='body2' color='text.secondary' gutterBottom>
        Sent to {recipients.length} recipient{recipients.length === 1 ? '' : 's'} (previews and samples excluded)
      </Typography>
      <TextField
        fullWidth
        variant='standard'
        placeholder='Search by email or name'
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Search color='action' />
            </InputAdornment>
          )
        }}
      />
      {
        filtered.length === 0
          ? (
            <Typography sx={{ py: 4 }} color='text.secondary'>
              {recipients.length === 0 ? 'No recipients found.' : 'No recipients match your search.'}
            </Typography>
            )
          : (
            <>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map(col => (
                      <TableCell
                        key={col.id}
                        sortDirection={orderBy === col.id ? orderByDirection : false}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? orderByDirection : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r, i) => (
                    <TableRow key={`${r.email}-${i}`} hover>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{`${r.firstName || ''} ${r.lastName || ''}`.trim() || '-'}</TableCell>
                      <TableCell>{r.sentAt ? moment(r.sentAt).format('MMMM Do YYYY, h:mm a') : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                component='div'
                count={filtered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              />
            </>
            )
      }
    </DialogContent>
  );
}

RecipientsContent.propTypes = {
  articleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};

// `article` can be null between menu selections, so the live read + title are guarded on it.
export default function ArticleRecipientsDialog ({ article, open, onClose }) {
  const title = article ? `Recipients — ${article.title}` : 'Recipients';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      {open && article ? <RecipientsContent articleId={article.id} /> : null}
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

ArticleRecipientsDialog.propTypes = {
  article: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
